import { defaultFilter, EmailRecipients, Entity, Filter } from "../../../actions/Admin/reducer";
import { Supplier, SupplierInfo } from "./Supplier/types";
import { FormField, FormRadioField, FormTextListField, RadioOption } from "../../../actions/Tenants/config/constantsTyped";
import { validateEmail } from "../../../utils";
import { Client, ClientInfo, ClientInfoFormObject } from "./Client/types";
import { PageBaseTableColumns } from "./PageBase";
import { SupplierInfoFormObject } from "./Supplier/defineSupplierInfoFormCreate";

export type EmailRecipientsTextList = { to: string[], cc: string[], bcc: string[] };

export const convertEmailRecipientTextListIntoObject = (value?: EmailRecipientsTextList): EmailRecipients => {
    const populate = (list: string[] = []) => list.map((email) => ({ address: email, displayName: email }));
    return {
        to: populate(value?.to),
        cc: populate(value?.cc),
        bcc: populate(value?.bcc),
    };
};

export const convertEmailRecipientsObjectIntoTextList = (value?: EmailRecipients): EmailRecipientsTextList => {
    const populate = (list: Array<{ address: string, displayName: string }> = []) => list.map((email) => email.address);
    return {
        to: populate(value?.to),
        cc: populate(value?.cc),
        bcc: populate(value?.bcc),
    };
};

export const filterResults = <T extends Entity>(results: T[] = [], filter: Filter) => {
    const offset = filter.offset || defaultFilter.offset as number;
    const limit = filter.limit || defaultFilter.limit as number;
    // check if offset and limit are within the bounds of the results
    if (offset < 0 || offset > results.length || limit <= 0) {
        // eslint-disable-next-line no-console
        console.error(`Invalid offset or limit: ${offset}, ${limit}`);
        return [];
    }
    return results.slice(filter.offset, offset + limit);
};

export const emailRecipientField: FormField = {
    label: "",
    name: "address",
    type: "email",
    validate: validateEmail
};

export const createEmailRecipientsField = (name: string, label?: string, disabled?: boolean): FormTextListField => ({
    label: label || "Email to:",
    name: name.toLowerCase(),
    type: "text-list",
    field: emailRecipientField,
    disabled
});

export const generateName = (label: string) => label.toLowerCase().replace(/ /g, "-");

export const getFormFields = (formOptions, type: "Supplier" | "Client") => {
    const fruitOptions = formOptions?.fruit_type ?? [];
    const countryOptions = formOptions?.country ?? [];

    const countryField: FormField = type === "Client"
        ? {
            label: "Countries",
            name: "countries",
            type: "single-select",
            mandatory: false,
            multi: true,
            return_array: true,
            options: countryOptions,
        }
        : {
            label: "Country",
            name: "country",
            type: "select-managed",
            mandatory: true,
        };

    const form: FormField[] = [
        {
            label: `${type} Name`,
            name: "label",
            type: "text",
            mandatory: true,
            pattern: "^(?!\\s*$).+$",
            invalid_hint: "String consists of spaces is not allowed",
        },
        countryField,
        {
            label: "Fruit(s)",
            name: "fruit_type",
            type: "single-select",
            mandatory: true,
            multi: true,
            return_array: true,
            options: fruitOptions
        },
    ];

    return form;
};

export const getInfoFormFields = (formOptions, entity) => {
    const optionAll: RadioOption = {
        value: "*",
        label: "All",
    };

    const reportTypeOptions = [optionAll, ...formOptions?.report_type || []];

    const fruitOptions = [optionAll, ...(entity?.fruit_types ?? []).map(
        (fruit_type) => ({
            value: fruit_type,
            label: (formOptions?.fruit_type ?? []).find((option) => option.value === fruit_type)?.label || fruit_type
        })
    )];

    const fruitTypeField: FormRadioField = {
        label: "Fruit(s)",
        name: "fruit_type",
        type: "radio",
        options: fruitOptions,
        default_value: fruitOptions?.[0]?.value,
    };

    const reportTypeField: FormRadioField = {
        label: "Report type",
        name: "report_type",
        type: "radio",
        options: reportTypeOptions,
        default_value: reportTypeOptions?.[0]?.value,
    };

    const infoForm: FormField[] = [
        fruitTypeField,
        reportTypeField,
        {
            label: "",
            name: "email_recipients",
            type: "inline-form",
            fields: [
                createEmailRecipientsField("To"),
            ],
        }
    ];

    return infoForm;
};

export const findInfoIndex = (entity: Partial<Supplier | Client>, fruit_type: string, report_type: string): number => {
    const fruitTypeMatches = (info) => (fruit_type === "*" ? !info.fruit_type : info.fruit_type === fruit_type);
    const reportTypeMatches = (info) => (report_type === "*" ? !info.report_type : info.report_type === report_type);

    return (entity.infos || []).findIndex((info) => {
        return fruitTypeMatches(info) && reportTypeMatches(info) && (info.report_status === "READY")
            && (info.supplier_id === entity.id || info.client_id === entity.id);
    });
};

export const getFormObjectFromEntity = (entity: Partial<Supplier | Client>, fruit_type = "*", report_type = "*"): Partial<SupplierInfoFormObject | ClientInfoFormObject> => {
    const info: Partial<SupplierInfo | ClientInfo> = (entity.infos || [])[findInfoIndex(entity, fruit_type, report_type)] || {};
    const { email_recipients } = info;

    return {
        ...info,
        fruit_type,
        report_type,
        email_recipients: convertEmailRecipientsObjectIntoTextList(email_recipients),
    };
};

export const getTableRow = (config, type: "Client" | "Supplier") => {
    const countryColumn: PageBaseTableColumns<Supplier | Client> = type === "Client"
        ? {
            label: "Countries",
            name: "countries",
            display_component(entity: Supplier | Client) {
                const countries = (entity as Client).countries ?? [];
                return countries.map((c) => config.get_form_option_label("country", c) || c).join(", ");
            },
            lg: 1,
        }
        : {
            label: "Country",
            name: "country",
            display_component(entity: Supplier | Client) {
                return config.get_form_option_label("country", (entity as Supplier).country);
            },
            lg: 1,
        };

    const tableRow: PageBaseTableColumns<Supplier | Client>[] = [
        {
            label: `${type} Name`,
            name: "label",
            lg: 2,
        },
        countryColumn,
        {
            label: "Fruit(s)",
            name: "fruit_types",
            display_component(entity: Supplier | Client) {
                return entity.fruit_types.map((fruit_type) => (config.get_form_option_label("fruit_type", fruit_type)) || []).join(", ");
            },
            lg: 1,
        }
    ];

    return tableRow;
};

export const getEmailRecipients = (entity: Client | Supplier) => {
    const email_recipients = ((entity?.infos as any) || []).reduce((acc, info) => {
        return {
            to: [...acc.to, ...info.email_recipients?.to || []],
            cc: [...acc.cc, ...info.email_recipients?.cc || []],
            bcc: [...acc.bcc, ...info.email_recipients?.bcc || []],
        };
    }, { to: [], cc: [], bcc: [] } as EmailRecipients);

    return email_recipients;
};

export const isCountryValid = (countries: any[], value: string) => !!countries?.find((c: any) => (c.label?.toLowerCase() === (value ?? "").toLowerCase() || c.value?.toLowerCase() === (value ?? "").toLowerCase()));

export const validateImportItems = (entityName, { existingItems, importData, config }) => {
    const grouped: Record<string, any> = {};
    const errors = {};
    const newEntities: string[] = [];

    const countries = config?.form_options?.country ?? [];
    const fruitTypes = config?.fruit_types ?? [];

    importData?.forEach((item, idx) => {
        const name = item?.name?.trim();
        const country = (item.country ?? "").trim()?.toLowerCase();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        // For clients, "countries" is a comma-separated list. Other entities still use single "country".
        const rawCountries = entityName === "client"
            ? (item.countries ?? "").split(",").map((c: string) => c.trim()).filter((c) => !!c)
            : [];
        const validCountriesForClient = entityName === "client"
            ? rawCountries
                .map((c: string) => countries?.find((opt: any) => opt.value?.toLowerCase() === c.toLowerCase())?.value)
                .filter((c: string | undefined): c is string => !!c)
            : [];

        const allRecipients = (item.emails ?? "").split(",").map((e: string) => e.trim()).filter((e) => !!e);
        const validEmailRecipients = allRecipients.filter((e: string) => emailRegex.test(e));

        const allFruits = (item.fruit_types ?? "").split(",").map((ft: string) => ft.trim()).filter((ft) => !!ft);
        const validFruitTypes = item.fruit_types
            ? (item.fruit_types ?? "")
                .split(",")
                .map((fruit_type: string) => fruit_type.trim()?.toLowerCase())
                .map((fruitType: string) => fruitTypes.find(
                    (f) => f.value?.toLowerCase() === fruitType
                ))
                .filter((f) => !!f)
                .map((f) => f.value)
            : undefined;

        const allReportTypes = (item.report_type ?? "").split(",").map((r: string) => r.trim().toLowerCase()).filter((r) => !!r);
        const validReportTypes = allReportTypes.map(
            (r: string) => config?.pdf_reports?.find(
                (cfg) => cfg.value?.toLowerCase() === r
            )?.value
        ).filter((r) => !!r);

        const uniqueIdentifier = entityName === "grower" ? item.ggn : name;
        if (!uniqueIdentifier) {
            errors[idx] = { ...(errors[idx] || {}), name: entityName === "grower" ? "Grower's ggn is required" : "Name is required" };
        }

        if (item.emails && (validEmailRecipients?.length !== allRecipients?.length)) {
            errors[idx] = { ...(errors[idx] || {}), emails: "One or more email addresses are not valid" };
        }

        if (entityName === "supplier" && item.ggn && !validFruitTypes.length) {
            errors[idx] = { ...(errors[idx] || {}), fruit_types: "At least one valid fruit type is required when a grower GGN is provided" };
        }

        if (entityName === "client") {
            if (rawCountries.length && validCountriesForClient.length !== rawCountries.length) {
                errors[idx] = { ...(errors[idx] || {}), countries: "One or more countries are not valid" };
            }
        } else if (country && !isCountryValid(countries, country)) {
            errors[idx] = { ...(errors[idx] || {}), country: "Country is not valid" };
        }

        if (item.fruit_types && (validFruitTypes?.length !== allFruits?.length)) {
            errors[idx] = { ...(errors[idx] || {}), fruit_types: "One or more fruit types are not valid" };
        }

        const entityExists = existingItems?.includes(uniqueIdentifier);
        const isProcessedNewEntity = newEntities.includes(uniqueIdentifier);

        if (!entityExists) {
            if (!isProcessedNewEntity) {
                if (!validFruitTypes?.length || (entityName === "grower" && (!name || !country || !isCountryValid(countries, country)))) {
                    errors[idx] = { ...(errors[idx] || {}), name: `Missing required fields to create new ${entityName}.` };
                } else {
                    newEntities.push(uniqueIdentifier);
                }
            }
        }

        if (allReportTypes?.length && (validReportTypes?.length !== allReportTypes?.length)) {
            errors[idx] = { ...(errors[idx] || {}), report_type: "One or more report types are not valid" };
        }

        if (entityName !== "client" && grouped[name] && (!!country && (country !== grouped[name].country))) {
            errors[idx] = { ...(errors[idx] || {}), country: `Duplicate ${entityName} name with different country` };
        }

        if (entityName === "grower" && grouped[item.ggn]) {
            errors[idx] = { ...(errors[idx] || {}), ggn: "GGN must be unique." };
        }

        if (errors[idx]) return; // skip invalid items

        if (!grouped[uniqueIdentifier]) {
            const baseGroup: any = {
                name,
                label: name,
                fruit_types: validFruitTypes,
                infos: [],
                growers: [],
                ggn: item.ggn ? String(item.ggn).trim() : undefined,
            };
            if (entityName === "client") {
                baseGroup.countries = validCountriesForClient;
            } else {
                baseGroup.country = countries?.find((c: any) => c.value?.toLowerCase() === country)?.value;
            }
            grouped[uniqueIdentifier] = baseGroup;
        } else if (entityName === "client" && validCountriesForClient.length) {
            grouped[uniqueIdentifier].countries = Array.from(
                new Set([...(grouped[uniqueIdentifier].countries ?? []), ...validCountriesForClient])
            );
        }

        const group = grouped[uniqueIdentifier];

        if (Array.isArray(validFruitTypes)) {
            group.fruit_types = Array.from(
                new Set([...(group.fruit_types ?? []), ...validFruitTypes])
            );
        }

        if (entityName === "supplier" && item.ggn && validFruitTypes.length) {
            // TODO check if ggn exists and if not create grower entries
            validFruitTypes.forEach((f) => group.growers.push({ fruit_type: f, ggn: item.ggn }));
        }

        if (validEmailRecipients?.length) {
            const reportTypes = (item.report_type ?? "")
                .split(",")
                .map((r: string) => r.trim().toLowerCase())
                .map(
                    (r: string) => config?.pdf_reports?.find(
                        (cfg) => cfg.text?.toLowerCase() === r
                        || cfg.value?.toLowerCase() === r
                    )?.value
                )
                .filter((r) => !!r);

            const reports = reportTypes.length ? reportTypes : [undefined];
            const fruits = validFruitTypes && validFruitTypes?.length > 0 ? validFruitTypes : [undefined];

            fruits.forEach((fruit) => {
                reports.forEach((rt) => {
                    group.infos.push({
                        fruit_type: fruit,
                        report_type: rt,
                        emails: validEmailRecipients,
                    });
                });
            });
        }
    });

    return { errors, validItems: Object.values(grouped) };
};
